// Globális hónap-rács naptár (design-rendszer szerint). Nincs külső naptár-függőség — a dátum-matek a `date-fns`,
// a hónap-/napnevek a locale-objektumból. Hétfő-kezdő rács (magyar/EU konvenció), a kiválasztott nap accent-kitöltés,
// a mai nap gyűrű, a hónapon kívüli napok halványak. A `DatePicker` (Popover) használja, de önállóan is beágyazható.
//
// HÁROM NÉZET (drill-down, a Google/Outlook naptárak mintája): a fejléc-CÍMKE (a két nyíl között) gomb —
// nap-rács → hónap-rács → év-rács, majd vissza. A nyilak a NÉZETHEZ igazodnak (hónap / év / 12-éves blokk), a
// hónap-/év-választás pedig egy szinttel visszalép, tehát a kiválasztás mindig a nap-rácsban záródik. A
// KOMPONENS SZERZŐDÉSE VÁLTOZATLAN: `onSelect` KIZÁRÓLAG napra hív (a köztes nézetek csak navigálnak), így a
// `DatePicker`/`DateTimePicker` és minden fogyasztó érintetlen.

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import {
  addMonths,
  addYears,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  getYear,
  isSameDay,
  isSameMonth,
  isToday,
  setMonth as setMonthOf,
  setYear as setYearOf,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths,
  subYears,
} from 'date-fns';
import { enGB } from 'date-fns/locale/en-GB';
import { hu } from 'date-fns/locale/hu';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

function localeObj(language: string): Locale {
  return language.toLowerCase().startsWith('en') ? enGB : hu;
}

type Locale = typeof hu;

/** A drill-down szintjei (a fejléc-címke ebben a körben lépteti). */
type CalendarView = 'days' | 'months' | 'years';

export interface CalendarProps {
  /** A kiválasztott nap (vagy null). */
  selected?: Date | null;
  onSelect: (date: Date) => void;
  /** UI-nyelv (a hónap-/napnevekhez); alap `hu`. */
  language?: string;
}

const WEEK_OPTS = { weekStartsOn: 1 } as const; // hétfő-kezdő
/** Az év-rács egy lapja (3 × 4) — a nyilak ennyivel lépnek év-nézetben. */
const YEARS_PER_PAGE = 12;

/** Az `year` blokk-kezdete (a 12-es lapozás stabil rácsa): pl. 2026 → 2016 (2016..2027). */
function yearBlockStart(year: number): number {
  return year - (year % YEARS_PER_PAGE);
}

export function Calendar({ selected, onSelect, language = 'hu' }: CalendarProps): JSX.Element {
  const { t } = useTranslation();
  const loc = localeObj(language);
  const [month, setMonth] = useState<Date>(() => startOfMonth(selected ?? new Date()));
  const [view, setView] = useState<CalendarView>('days');

  const gridStart = startOfWeek(startOfMonth(month), WEEK_OPTS);
  const gridEnd = endOfWeek(endOfMonth(month), WEEK_OPTS);
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const weekdays = days.slice(0, 7);

  const blockStart = yearBlockStart(getYear(month));

  // A fejléc három dolga NÉZET-FÜGGŐ: a címke, a nyilak lépése és a nyilak a11y-neve.
  const caption =
    view === 'days'
      ? format(month, 'yyyy. LLLL', { locale: loc })
      : view === 'months'
        ? format(month, 'yyyy.', { locale: loc })
        : `${blockStart}–${blockStart + YEARS_PER_PAGE - 1}`;

  const step = (dir: -1 | 1): void => {
    setMonth((m) => {
      if (view === 'days') return dir === 1 ? addMonths(m, 1) : subMonths(m, 1);
      if (view === 'months') return dir === 1 ? addYears(m, 1) : subYears(m, 1);
      return dir === 1 ? addYears(m, YEARS_PER_PAGE) : subYears(m, YEARS_PER_PAGE);
    });
  };

  const prevLabel =
    view === 'days'
      ? t('common.calendar.prevMonth')
      : view === 'months'
        ? t('common.calendar.prevYear')
        : t('common.calendar.prevYears');
  const nextLabel =
    view === 'days'
      ? t('common.calendar.nextMonth')
      : view === 'months'
        ? t('common.calendar.nextYear')
        : t('common.calendar.nextYears');
  // A címke a KÖVETKEZŐ szintet nevezi meg (amit a kattintás megnyit) — nap-rácson „hónap választása".
  const captionLabel =
    view === 'days'
      ? t('common.calendar.pickMonth')
      : view === 'months'
        ? t('common.calendar.pickYear')
        : t('common.calendar.pickDay');

  return (
    <div className="w-[15.5rem] select-none">
      {/* Fejléc: nézet-hű navigáció + a drill-downt léptető címke-gomb. */}
      <div className="mb-2 flex items-center justify-between">
        <NavButton onClick={() => step(-1)} aria-label={prevLabel}>
          <ChevronLeftIcon className="size-4" />
        </NavButton>
        <button
          type="button"
          aria-label={captionLabel}
          onClick={() =>
            setView((v) => (v === 'days' ? 'months' : v === 'months' ? 'years' : 'days'))
          }
          className="text-md text-foreground hover:bg-accent/60 focus-visible:ring-accent-600 rounded-md px-2 py-0.5 font-medium capitalize transition-colors focus-visible:outline-none focus-visible:ring-2"
        >
          {caption}
        </button>
        <NavButton onClick={() => step(1)} aria-label={nextLabel}>
          <ChevronRightIcon className="size-4" />
        </NavButton>
      </div>

      {view === 'days' && (
        <>
          {/* Napnév-fejsor (2 betűs, hétfő-kezdő). */}
          <div className="mb-1 grid grid-cols-7 gap-0.5">
            {weekdays.map((d) => (
              <div
                key={`wd-${d.getTime()}`}
                className="text-subtle flex h-7 items-center justify-center text-xs font-medium capitalize"
              >
                {format(d, 'EEEEEE', { locale: loc })}
              </div>
            ))}
          </div>

          {/* Nap-rács. */}
          <div className="grid grid-cols-7 gap-0.5">
            {days.map((day) => {
              const isSelected = selected ? isSameDay(day, selected) : false;
              const inMonth = isSameMonth(day, month);
              const today = isToday(day);
              return (
                <button
                  key={day.getTime()}
                  type="button"
                  onClick={() => onSelect(day)}
                  aria-label={format(day, 'yyyy. MM. dd.')}
                  aria-current={today ? 'date' : undefined}
                  className={cn(
                    'focus-visible:ring-accent-600 text-md flex h-8 items-center justify-center rounded-md tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2',
                    isSelected
                      ? 'bg-accent-600 hover:bg-accent-600 dark:bg-accent-400 font-medium text-white dark:text-black'
                      : cn(
                          'hover:bg-accent/60',
                          inMonth ? 'text-foreground' : 'text-subtle',
                          today && 'ring-accent-600/50 dark:ring-accent-400/50 font-medium ring-1',
                        ),
                  )}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>
        </>
      )}

      {view === 'months' && (
        <div className="grid grid-cols-3 gap-1">
          {Array.from({ length: 12 }, (_, i) => setMonthOf(startOfYear(month), i)).map((m) => (
            <GridCell
              key={m.getTime()}
              active={selected !== null && selected !== undefined && isSameMonth(m, selected)}
              current={isSameMonth(m, month)}
              onClick={() => {
                setMonth(startOfMonth(m));
                setView('days');
              }}
            >
              {format(m, 'LLL', { locale: loc })}
            </GridCell>
          ))}
        </div>
      )}

      {view === 'years' && (
        <div className="grid grid-cols-3 gap-1">
          {Array.from({ length: YEARS_PER_PAGE }, (_, i) => blockStart + i).map((y) => (
            <GridCell
              key={y}
              active={selected ? getYear(selected) === y : false}
              current={getYear(month) === y}
              onClick={() => {
                setMonth((m) => startOfMonth(setYearOf(m, y)));
                setView('months');
              }}
            >
              {y}
            </GridCell>
          ))}
        </div>
      )}
    </div>
  );
}

/** Hónap-/év-rács cellája: `active` = a KIVÁLASZTOTT dátumhoz tartozik, `current` = épp ezt navigáljuk. */
function GridCell({
  children,
  active,
  current,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  current: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'focus-visible:ring-accent-600 text-md flex h-9 items-center justify-center rounded-md capitalize tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2',
        active
          ? 'bg-accent-600 hover:bg-accent-600 dark:bg-accent-400 font-medium text-white dark:text-black'
          : cn(
              'hover:bg-accent/60 text-foreground',
              current && 'ring-accent-600/50 dark:ring-accent-400/50 font-medium ring-1',
            ),
      )}
    >
      {children}
    </button>
  );
}

/** Fejléc-navigációs ikon-gomb (nézet-hű lépés: hónap / év / 12-éves blokk). */
function NavButton({
  children,
  onClick,
  'aria-label': ariaLabel,
}: {
  children: React.ReactNode;
  onClick: () => void;
  'aria-label': string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className="text-muted-foreground hover:bg-accent/60 hover:text-foreground focus-visible:ring-accent-600 flex size-7 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2"
    >
      {children}
    </button>
  );
}
